import React, { useContext, useEffect, useState } from 'react';
import { IsNullOrEmpty, IsSelectEmpty, toastMessage } from '../../core/utility';
import { post } from '../../core/client';
import { ResponseBase } from '../../models/shared/Models';
import { MakePurchaseRequest, MemberAv } from '../../models/query-member';
import { ListModel, RelationTypeItem } from '../../models/shared/Models';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Help } from '@mui/icons-material';
import { QueryMemberContext } from './Context';
import Select from '../../components/inputs/select';
import Input from '../../components/inputs/text-field-input';
import InfoTooltip from '../../components/tooltip';
import { ClearButton, CustomButton } from '../../components/buttons';
import { Alert, CircularProgress } from '@mui/material';

interface PurchaseProps {
    productLoading: boolean;
    onNewPurchase: () => void;
}

interface InputErrorModel {
    name: string;
    errorText: string;
}

const initialMakePurchaseState: MakePurchaseRequest = {
    avNo: '',
    companyAvId: '',
    productId: null,
    productName: null,
    amount: null,
    discountAmount: null,
    relationTypeId: null,
    invoiceFullName: ''
};

const MAX_PURCHASE_AMOUNT = 999_999.99;

const Purchase = ({ productLoading, onNewPurchase }: PurchaseProps) => {

    //context
    const { member, products } = useContext(QueryMemberContext);

    const avNo = (member as MemberAv).avNo;

    // states   
    const [makePurchaseState, setMakePurchaseState] = useState<MakePurchaseRequest>(initialMakePurchaseState);
    const [loading, setLoading] = useState(false);
    const [inputErrors, setInputErrors] = useState<Array<InputErrorModel>>([]);
    const [purchaseCompleted, setPurchaseCompleted] = useState(false);
    const [relationTypes, setRelationTypes] = useState<Array<RelationTypeItem>>([]);

    // hooks
    useEffect(() => {
        if (member) {
            loadRelationTypes(member.memberTypeId);
        }
    }, [])

    // methods
    const loadRelationTypes = async (memberTypeId: number) => {
        const request = {
            memberTypeId: memberTypeId,
        };
        const response = await post<ListModel<RelationTypeItem>>('api/dealer/list-av-member-relation-types', request);
        setRelationTypes(response.items);
    }

    const makePurchase = async (model: MakePurchaseRequest) => {

        if (!validate(model)) return;

        if (IsNullOrEmpty(member?.avNo)) {
            toastMessage({
                messageType: 'error',
                title: "Alışveriş No bulunamadı. Tekrar deneyiniz."
            });
            return;
        }

        setLoading(true);
        const request: MakePurchaseRequest = {
            ...model,
            avNo: avNo,
            companyAvId: model.companyAvId === '' ? avNo : model.companyAvId
        };
        const response = await post<ResponseBase>('api/dealer/make-purchase', request);
        setLoading(false);
        if (response != null) {
            if (response.resultCode === "1") {
                setPurchaseCompleted(true);
            }
            else {
                toastMessage({
                    messageType: 'error',
                    title: "ALIŞVERİŞ SONUCU",
                    message: response.description,
                });
            }
        }
    };

    const handleNullableNumberChange = (e: any) => {
        const val = e.target.value;
        setMakePurchaseState(prevState => ({
            ...prevState,
            [e.target.name]: val === '' ? null : val,
        }));
    };

    const handleProductChange = (e: any) => {
        const product = products?.products.find(c => c.campaignProductId === e.target.value);
        if (product) {
            setMakePurchaseState(prev => ({
                ...prev,
                productId: product.campaignProductId,
                productName: product.campaignProductName,
                companyAvId: product.campaignCompanyId.toString(),
            }));
        }
    };

    const handleChange = (e: any) => {
        setMakePurchaseState(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    }

    const handleNewPurchase = () => {
        setPurchaseCompleted(false);
        setMakePurchaseState(initialMakePurchaseState);
        onNewPurchase();
    }

    const handleClear = () => {
        setMakePurchaseState(initialMakePurchaseState);
    }

    const validate = (model: MakePurchaseRequest): boolean => {

        setInputErrors([]);
        let isValid = true;

        const product = products?.products.find(x => x.campaignProductId === model.productId) ?? null;

        if (model.amount === null) {
            const error: InputErrorModel = {
                name: 'amount',
                errorText: 'Bu alan boş bırakılamaz'
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        else if (product?.maxPrice && model.amount > product.maxPrice) {
            const error: InputErrorModel = {
                name: 'amount',
                errorText: `Girilebilecek max tutar: ${product.maxPrice}`
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        else if (model.amount > MAX_PURCHASE_AMOUNT){
            const error: InputErrorModel = {
                name: 'amount',
                errorText: `Girilebilecek max tutar: ${MAX_PURCHASE_AMOUNT}`
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        if(model.discountAmount !== null && model.amount && model.discountAmount > model.amount){
            const error: InputErrorModel = {
                name: 'discountAmount',
                errorText: `İndirim tutarı satış tutarından büyük olamaz`
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        if(model.discountAmount !== null && model.discountAmount < 0){
            const error: InputErrorModel = {
                name: 'discountAmount',
                errorText: `İndirim tutarı negatif bir değer olamaz`
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        if (model.discountAmount === null) {
            const error: InputErrorModel = {
                name: 'discountAmount',
                errorText: 'Bu alan boş bırakılamaz'
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        if (IsSelectEmpty(model.productName)) {
            const error: InputErrorModel = {
                name: 'productName',
                errorText: 'Ürün seçiniz'
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        if (IsSelectEmpty(model.relationTypeId)) {
            const error: InputErrorModel = {
                name: 'relationTypeId',
                errorText: 'Yakınlık derecesi seçiniz'
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        if (IsNullOrEmpty(model.invoiceFullName)) {
            const error: InputErrorModel = {
                name: 'invoiceFullName',
                errorText: 'Fatura Ad Soyad giriniz'
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }

        return isValid;
    }

    return (
        <>
            {!purchaseCompleted ?
                <React.Fragment>
                    <Box mb={2}>
                        {productLoading || (products && products.products.length > 0) ? (
                            <>
                                <Select
                                    id="campaignProductId"
                                    name="campaignProductId"
                                    onChange={handleProductChange}
                                    caption="Ürün"
                                    className="densed-select"
                                    disabled={purchaseCompleted}
                                    loading={productLoading}
                                    options={{
                                        data: products?.products ?? [],
                                        displayField: 'campaignProductName',
                                        displayValue: 'campaignProductId',
                                        selected: makePurchaseState.productId,
                                    }}
                                    errorText={inputErrors.filter(x => x.name === 'productName')[0]?.errorText}
                                />
                                {products?.hasUnsufficientProductQuota && <Alert severity='warning' className='alert-small mt-1'>Sadece kotası olan ürünler görünür.</Alert>}
                            </>
                        ) : (
                            <Input
                                id="productName"
                                name="productName"
                                caption="Ürün"
                                disabled={purchaseCompleted||(products?.hasUnsufficientProductQuota&&products?.hasAvailableQuota)}
                                value={makePurchaseState.productName}
                                endAdornment={productLoading ? <CircularProgress /> : null}
                                onChange={handleChange}
                                errorText={inputErrors.filter(x => x.name === 'productName')[0]?.errorText}
                            />
                        )}
                    </Box>
                    <Box mb={2}>
                        <Input
                            caption="Satış Toplamı"
                            id="amount"
                            name="amount"
                            disabled={purchaseCompleted}
                            placeholder="0.00"
                            numericFormat={{
                                decimalScale: 2,
                                fixedDecimalScale: true,
                                decimalSeparator: ',',
                                thousandSeparator: '.',
                            }}
                            endAdornment={
                                <InfoTooltip title="Üyenin ödediği tutar"
                                    arrow placement='left' disableFocusListener>
                                    <Help className='tooltip-icon' />
                                </InfoTooltip>
                            }
                            value={makePurchaseState.amount || ''}
                            onChange={handleNullableNumberChange}
                            errorText={inputErrors.filter(x => x.name === 'amount')[0]?.errorText}
                        />
                    </Box>
                    <Box mb={2}>
                        <Input
                            caption="İndirim Tutarı"
                            id="discountAmount"
                            name="discountAmount"
                            disabled={purchaseCompleted}
                            placeholder="0.00"
                            numericFormat={{
                                decimalScale: 2,
                                fixedDecimalScale: true,
                                decimalSeparator: ',',
                                thousandSeparator: '.'
                            }}
                            endAdornment={
                                <InfoTooltip title="Liste fiyatı üzerinde uygulanan indirim tutarı"
                                    arrow placement='left' disableFocusListener>
                                    <Help className='tooltip-icon' />
                                </InfoTooltip>
                            }
                            value={makePurchaseState.discountAmount || ''}
                            onChange={handleNullableNumberChange}
                            errorText={inputErrors.filter(x => x.name === 'discountAmount')[0]?.errorText}
                        />
                    </Box>
                    <Box mb={2}>
                        <Select
                            id="relationTypeId"
                            name="relationTypeId"
                            onChange={handleChange}
                            caption="Yakınlık Derecesi"
                            className="densed-select"
                            disabled={purchaseCompleted}
                            options={{
                                data: relationTypes,
                                displayField: 'relationTypeName',
                                displayValue: 'relationTypeId',
                                selected: makePurchaseState.relationTypeId,
                            }}
                            errorText={inputErrors.filter(x => x.name === 'relationTypeId')[0]?.errorText}
                        />
                    </Box>
                    <Box mb={2}>
                        <Input
                            id="invoiceFullName"
                            name="invoiceFullName"
                            caption="Fatura Ad Soyad"
                            toUpperCase
                            disabled={purchaseCompleted}
                            value={makePurchaseState.invoiceFullName}
                            onChange={handleChange}
                            errorText={inputErrors.filter(x => x.name === 'invoiceFullName')[0]?.errorText}
                        />
                    </Box>
                    <Grid container spacing={3} direction="column" alignItems="center">
                        <Grid item>
                            <CustomButton
                                text="ALIŞVERİŞİ TAMAMLA"
                                className='btn-primary mr-2'
                                disabled={loading}
                                loading={loading}
                                onClick={() => makePurchase(makePurchaseState)}
                            />
                            <ClearButton
                                disabled={loading}
                                onClick={handleClear}
                            />
                        </Grid>
                    </Grid>
                </React.Fragment>
                : <div className="purchase-success">
                    <Grid container spacing={3} direction="column" justifyContent="center" alignItems="center">
                        <Grid item style={{ textAlign: 'center' }}>
                            <h3 className="mb-4">İşlem Başarılı!</h3>
                            <p className="mb-4" style={{ fontSize: 16 }}>Alışveriş tamamlanmıştır</p>
                            <h5><strong>Av No: {avNo}</strong></h5>
                        </Grid>
                        <Grid item>
                            <CustomButton
                                text="Yeni Alışveriş"
                                className='btn-primary'
                                onClick={() => handleNewPurchase()}
                            />
                        </Grid>
                    </Grid>
                </div>
            }
        </>
    )
}

export default Purchase;
